import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Collapse,
  Chip,
  IconButton,
  Slide,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Lock as LockIcon,
  Tune as TuneIcon,
  BarChart as ChartIcon,
} from '@mui/icons-material';

interface CookieConsent {
  necessary: boolean;
  functional: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

const CONSENT_VERSION = '1.0';
const STORAGE_KEY = 'cookieConsent';
// 12 months — aligns with EU/GDPR common practice (Google, IKEA, CNIL guidance).
// After this period the banner reappears so the user can renew their decision.
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

const CookieConsentBanner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true,
    functional: false,
    marketing: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  });

  useEffect(() => {
    // Check if consent is already saved
    const savedConsent = localStorage.getItem(STORAGE_KEY);

    if (savedConsent) {
      try {
        const parsed: CookieConsent = JSON.parse(savedConsent);

        // Re-ask if the legal text version changed.
        if (parsed.version !== CONSENT_VERSION) {
          setTimeout(() => setShowBanner(true), 500);
          return;
        }

        // Re-ask if the consent is older than the max age (12 months).
        const savedAt = parsed.timestamp ? new Date(parsed.timestamp).getTime() : 0;
        if (!savedAt || Date.now() - savedAt > CONSENT_MAX_AGE_MS) {
          setTimeout(() => setShowBanner(true), 500);
          return;
        }

        setConsent(parsed);
        setShowBanner(false);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
        setTimeout(() => setShowBanner(true), 500);
      }
    } else {
      // No consent saved, show banner with delay
      setTimeout(() => setShowBanner(true), 500);
    }
  }, []);

  const saveConsent = (newConsent: CookieConsent) => {
    setIsAnimating(true);

    const consentWithTimestamp = {
      ...newConsent,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentWithTimestamp));
    setConsent(consentWithTimestamp);

    // Animate out
    setTimeout(() => {
      setShowBanner(false);
      setShowSettings(false);
      setIsAnimating(false);
    }, 300);
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  };

  const handleAcceptNecessary = () => {
    saveConsent({
      necessary: true,
      functional: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
  };

  const handleToggle = (category: 'functional' | 'marketing') => {
    setConsent(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <>
      {/* Main Banner */}
      <Slide direction="up" in={showBanner} timeout={500}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            display: showBanner ? 'block' : 'none',
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.08), 0 -2px 8px rgba(0, 0, 0, 0.04)',
              py: { xs: 2, sm: 2.5 },
              px: { xs: 2, sm: 3 },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 },
              }}>
                {/* Cookie Icon with gradient */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #14161F 0%, #C7351F 100%)',
                    boxShadow: '0 4px 12px rgba(199,88,63,0.22)',
                    flexShrink: 0,
                  }}
                >
                  <CookieIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      mb: 0.5,
                      background: 'linear-gradient(135deg, #C7351F 0%, #F2A93B 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Sütiket használunk 🍪
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      lineHeight: 1.5,
                    }}
                  >
                    Weboldalunk sütiket használ a biztonságos böngészés és a legjobb felhasználói élmény biztosítása érdekében.
                  </Typography>

                  {/* Cookie Categories Chips */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip
                      icon={<LockIcon sx={{ fontSize: 16 }} />}
                      label="Szükséges"
                      size="small"
                      sx={{
                        bgcolor: '#14161F',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                    <Chip
                      icon={<TuneIcon sx={{ fontSize: 16 }} />}
                      label="Funkcionális"
                      size="small"
                      color={consent.functional ? 'primary' : 'default'}
                      variant={consent.functional ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                    />
                    <Chip
                      icon={<ChartIcon sx={{ fontSize: 16 }} />}
                      label="Marketing"
                      size="small"
                      color={consent.marketing ? 'secondary' : 'default'}
                      variant={consent.marketing ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                    />
                  </Box>

                  <Collapse in={showDetails}>
                    <Box sx={{
                      mb: 1.5,
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'rgba(199,88,63,0.04)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'rgba(199,88,63,0.15)',
                    }}>
                      <Typography variant="body2" paragraph sx={{ mb: 1, fontSize: '0.813rem' }}>
                        <strong>✓ Szükséges sütik:</strong> A weboldal működéséhez elengedhetetlenek
                        (bejelentkezés, kosár). Nem kapcsolhatók ki.
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ mb: 1, fontSize: '0.813rem' }}>
                        <strong>⚙ Funkcionális sütik:</strong> Kedvenc beállításaid tárolása
                        (nyelv, megjelenés), jobb felhasználói élmény.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0, fontSize: '0.813rem' }}>
                        <strong>📊 Marketing sütik:</strong> Látogatási statisztikák,
                        hirdetések személyre szabása, teljesítmény mérése.
                      </Typography>
                    </Box>
                  </Collapse>

                  <Button
                    size="small"
                    onClick={() => setShowDetails(!showDetails)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: showDetails ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    }
                    sx={{
                      fontSize: '0.813rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 1,
                      minWidth: 'auto',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(199,88,63,0.08)',
                      },
                    }}
                  >
                    {showDetails ? 'Kevesebb' : 'Részletek'}
                  </Button>
                </Box>

                {/* Close button (mobile) */}
                {isMobile && (
                  <IconButton
                    size="small"
                    onClick={handleAcceptNecessary}
                    sx={{
                      mt: -0.5,
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                flexDirection: { xs: 'column', sm: 'row' },
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowSettings(true)}
                  startIcon={<SettingsIcon />}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 1.5,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    py: { xs: 1, sm: 0.75 },
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderWidth: 1.5,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  Beállítások
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleAcceptNecessary}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 1.5,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    py: { xs: 1, sm: 0.75 },
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderWidth: 1.5,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  Csak szükségesek
                </Button>

                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  startIcon={<CheckIcon />}
                  fullWidth={isMobile}
                  disabled={isAnimating}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    py: { xs: 1, sm: 0.75 },
                    px: 3,
                    background: 'linear-gradient(135deg, #14161F 0%, #C7351F 100%)',
                    boxShadow: '0 4px 14px rgba(199,88,63,0.28)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #C7351F 0%, #F2A93B 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(199,88,63,0.35)',
                    },
                  }}
                >
                  Összes elfogadása
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Slide>

      {/* Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #14161F 0%, #C7351F 100%)',
                  boxShadow: '0 4px 12px rgba(199,88,63,0.22)',
                }}
              >
                <SettingsIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                Süti beállítások
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowSettings(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.necessary}
                  disabled
                  color="primary"
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <LockIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Szükséges sütik
                    </Typography>
                    <Chip
                      label="Kötelező"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: '#14161F',
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    A weboldal működéséhez elengedhetetlenek. Nem kapcsolhatók ki.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.functional}
                  onChange={() => handleToggle('functional')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Funkcionális sütik
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Beállításaid tárolása (nyelv, megjelenés), jobb felhasználói élmény.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.marketing}
                  onChange={() => handleToggle('marketing')}
                  color="secondary"
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <ChartIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Marketing és Analitika sütik
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Statisztikák, hirdetések személyre szabása, teljesítmény mérése.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setShowSettings(false)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            startIcon={<CheckIcon />}
            disabled={isAnimating}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              background: 'linear-gradient(135deg, #14161F 0%, #C7351F 100%)',
              boxShadow: '0 4px 14px rgba(199,88,63,0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #C7351F 0%, #F2A93B 100%)',
                boxShadow: '0 6px 20px rgba(199,88,63,0.35)',
              },
            }}
          >
            Beállítások mentése
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
